import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { API_BASE } from "@/api/client";
import {
  queryKeys,
  useCreateExport,
  useExportJob,
  useExportPreview,
  useExports,
  useProjects,
} from "@/api/queries";
import type { ExportJob } from "@/api/types";
import { Panel, SectionTitle } from "@/components/Panel";
import { RemoteState } from "@/components/RemoteState";
import { StatusLabel } from "@/components/StatusLabel";
import { Tag } from "@/components/Tag";
import { formatBytes } from "@/lib/cn";
import { useUiStore } from "@/store/ui";

/** @dataSource remote */

type ExportFormat = "markdown" | "json" | "yaml" | "docx";

const FORMATS: { value: ExportFormat; label: string; hint: string }[] = [
  { value: "docx", label: "Word", hint: "保留审阅批注层级" },
  { value: "markdown", label: "Markdown", hint: "便于版本对比" },
  { value: "json", label: "JSON", hint: "机器可读，含版本字段" },
  { value: "yaml", label: "YAML", hint: "与夹具同源" },
];

// 导出任务状态取自 `ExportJob.status`：初始 `QUEUED`，成功写 `SUCCEEDED`，
// 失败写 `FAILED`（`app/services/exporter.py`）。这里不凭空添加后端未产生的状态。
const SUCCEEDED = new Set(["SUCCEEDED"]);
const FAILED = new Set(["FAILED"]);
/** 已有结论的状态：到达即停止轮询。 */
const SETTLED = new Set([...SUCCEEDED, ...FAILED]);
/** 进行中任务的轮询间隔。 */
const POLL_MS = 3_000;

function newIdempotencyKey(format: string): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `export-${format}-${random}`;
}

/** 导出交付（specs/07 §19）。交付包携带产物/Profile/方法包版本与关口记录，可自证来源。 */
export function ExportView() {
  const projectId = useUiStore((state) => state.projectId);
  const pushToast = useUiStore((state) => state.pushToast);
  const exportsQuery = useExports(projectId);
  const previewQuery = useExportPreview(projectId);
  const createExport = useCreateExport(projectId);
  const projects = useProjects();

  const project = (projects.data ?? []).find((item) => item.id === projectId);
  const preview = previewQuery.data;

  return (
    <RemoteState queries={[exportsQuery, previewQuery]} title="导出数据加载失败">
      <div className="page-scroll">
        <div className="page-width">
          <header className="page-heading">
            <div>
              <span className="eyebrow">交付</span>
              <h2>交付包携带来源，可自证版本</h2>
            </div>
            <div className="meta-row">
              <Tag tone="blue">方法包 {project?.methodology_version ?? "—"}</Tag>
              <Tag tone="green">Profile {project?.locked_profile_version ?? "—"}</Tag>
            </div>
          </header>

          {preview ? (
            <Panel title="将写入交付包的内容" bodyClassName="funnel">
              <div className="funnel-step">
                <strong>
                  {preview.approved} / {preview.artifacts}
                </strong>
                <span>已批准产物</span>
              </div>
              <div className="funnel-step">
                <strong>{preview.draft}</strong>
                <span>草稿产物（会标注为待审阅）</span>
              </div>
              <div className="funnel-step">
                <strong>{preview.stale}</strong>
                <span>已失效产物</span>
              </div>
              <div className="funnel-step">
                <strong>
                  {preview.storylets} / {preview.choices} / {preview.scenes}
                </strong>
                <span>Storylet / 选择 / 分场</span>
              </div>
            </Panel>
          ) : null}

          <SectionTitle title="选择交付格式" />

          <div className="export-grid">
            {FORMATS.map((format) => (
              <button
                key={format.value}
                type="button"
                className="export-card"
                disabled={!projectId || createExport.isPending}
                onClick={() =>
                  createExport.mutate(
                    { format: format.value, idempotencyKey: newIdempotencyKey(format.value) },
                    {
                      onSuccess: (job) =>
                        pushToast({
                          title: `${format.label} 导出已创建`,
                          detail: `任务 ${job.id}`,
                          mark: "✓",
                        }),
                      onError: (error) =>
                        pushToast({
                          title: "导出失败",
                          detail: error instanceof Error ? error.message : "未知错误",
                          mark: "!",
                        }),
                    },
                  )
                }
              >
                <strong>{format.label}</strong>
                <small>{format.hint}</small>
              </button>
            ))}
          </div>

          <SectionTitle title="导出记录" />

          <Panel title="历史交付" bodyClassName="idea-list">
            {(exportsQuery.data ?? []).length === 0 ? (
              <div className="empty-state">
                <strong>还没有导出记录</strong>
                <p>选择上方任意格式，基于当前正式版本生成交付包。</p>
              </div>
            ) : (
              <ul className="activity-list">
                {(exportsQuery.data ?? []).map((job) => (
                  <ExportRow key={job.id} job={job} projectId={projectId} />
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </RemoteState>
  );
}

/**
 * 单条导出记录。
 *
 * 未到终态时按 `GET /exports/{id}` **只轮询这一条**，而不是整张历史——
 * 一条还在排队的导出不需要让整个列表跟着刷新。
 * 到达终态即停，并刷新一次列表，使历史区与本行结论一致。
 *
 * 刷新失败会就地说明，而不是悄悄继续展示旧状态（RULES §11.2）。
 */
function ExportRow({ job, projectId }: { job: ExportJob; projectId: string }) {
  const client = useQueryClient();
  const [pollMs, setPollMs] = useState<number | undefined>(
    SETTLED.has(job.status) ? undefined : POLL_MS,
  );
  const live = useExportJob(job.id, pollMs);
  const current = live.data ?? job;
  const status = current.status;
  const settled = SETTLED.has(status);
  const alreadySettled = useRef(false);

  useEffect(() => {
    if (!settled || alreadySettled.current) return;
    alreadySettled.current = true;
    setPollMs(undefined);
    void client.invalidateQueries({ queryKey: queryKeys.exports(projectId) });
  }, [settled, client, projectId]);

  const done = SUCCEEDED.has(status);
  const failed = FAILED.has(status);

  return (
    <li className="activity-item">
      <span className="mini-mark">↥</span>
      <div>
        <p>
          {current.format.toUpperCase()} · {current.id}
        </p>
        <small>
          {formatBytes(current.size_bytes)} · {current.artifact_ids.length} 个产物
        </small>
        {live.isError ? (
          <small style={{ color: "var(--red)" }}>
            状态刷新失败：
            {live.error instanceof Error ? live.error.message : "未知错误"}
          </small>
        ) : null}
      </div>
      {/*
        下载走**浏览器导航**（<a href>）而不是 fetch：这是 client.ts 之外的**唯一豁免**——
        它不读数据、只让浏览器按 `Content-Disposition: attachment` 存盘（specs/15 §5）。
        此前这里只把服务器本地路径当文字显示，交付物实际取不走。
      */}
      {done ? (
        <a className="button button-quiet" href={`${API_BASE}/exports/${current.id}/download`}>
          下载
        </a>
      ) : null}
      <StatusLabel tone={failed ? "danger" : done ? "success" : "warning"}>
        {failed ? "失败" : done ? "可下载" : "生成中"}
      </StatusLabel>
    </li>
  );
}
